import { useEffect, useState } from "react";
import { buildClasses } from "./utils/buildClasses";
import { startAppEntrance } from "./rye/riseIn";
import crustMask from "./images/bread_logo_crust_mask.png";
import crumbMask from "./images/bread_logo_crumb_mask.png";

/**
 * How long the logo takes to sink and fade once loading finishes. Keep in
 * step with the loading screen's leaving fade below, which ends at 550ms.
 */
const LEAVE_MS = 570;

/**
 * Shows the loading screen until `ready`, then hands off to `children`. For
 * the app, the logo sinks back into the background while it rises in
 * beneath; anything else waits for the logo to finish leaving.
 */
export function AppEntrance(props: {
  ready: boolean;
  /**
   * Whether `children` is the app, which rises in under the leaving logo.
   * Other screens, like checkout, don't, and overlapping them looks broken.
   */
  entersApp: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const [phase, setPhase] = useState<"loading" | "leaving" | "done">("loading");

  useEffect(() => {
    if (!props.ready || phase !== "loading") return;
    if (props.entersApp) startAppEntrance();
    setPhase("leaving");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ready]);

  useEffect(() => {
    if (phase !== "leaving") return;
    const timer = setTimeout(() => setPhase("done"), LEAVE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  return (
    <>
      {(phase === "done" || (phase === "leaving" && props.entersApp)) &&
        props.children}
      {phase !== "done" && (
        <FullPageLoadingScreen leaving={phase === "leaving"} />
      )}
    </>
  );
}

/**
 * The logo, carved from the page in two levels: the crust stands highest,
 * and the crumb is a second, lower surface inside it. It rises out of the page
 * into a panel's face and shadow, and sinks back flat as the app arrives.
 *
 * Rising, each level's shadow grows out from under its edges, as a lifting
 * object's does, while its face lights up from the page's cream. Sinking
 * reverses both, so the logo merges back into the page. The logo also comes
 * a touch closer as it rises, and drops back and softens as it dissolves.
 */
function FullPageLoadingScreen(props: { leaving: boolean }): JSX.Element {
  const level = buildClasses(
    "absolute",
    "inset-0",
    // Cast by a wrapper, as a filter on the stenciled face would be cut away
    // with everything else outside its shape.
    "drop-shadow-loading-logo",
    {
      if: props.leaving,
      then: "motion-safe:animate-logo-shadow-sink",
      else: "motion-safe:animate-logo-shadow-rise",
    },
  );
  const face = props.leaving
    ? "motion-safe:animate-logo-face-dim"
    : "motion-safe:animate-logo-face-light";
  return (
    <div
      role="status"
      aria-label="Loading"
      className={buildClasses(
        "fixed",
        "inset-0",
        "z-[100]",
        "flex",
        "items-center",
        "justify-center",
        "pointer-events-none",
        {
          if: props.leaving,
          // Once flat it's invisible on the page, but not over the rising
          // panel.
          then: buildClasses(
            "opacity-0",
            "transition-opacity",
            "duration-[250ms]",
            "delay-[300ms]",
            "ease-linear",
          ),
        },
      )}
    >
      {/* The stencils are 600px, so they stay sharp well past this size. */}
      <div
        aria-hidden
        className={buildClasses(
          "relative",
          "size-[300px]",
          // Kept on its own layer throughout, so the logo isn't redrawn the
          // moment the scale stops. That redraw snapped the old wordmark's
          // edges sharper, a visible twitch.
          "will-change-transform",
          {
            if: props.leaving,
            then: "motion-safe:animate-logo-dissolve",
            else: "motion-safe:animate-logo-lift",
          },
        )}
      >
        {/* The lower level first, so the crust casts its shadow onto it. It
        sits lower, so it catches a little less light than the crust, which
        is the panels' surface colour. */}
        <div className={level}>
          <LogoLevelFace
            mask={crumbMask}
            className={buildClasses("bg-[#fdfcf9]", face)}
          />
        </div>
        <div className={level}>
          <LogoLevelFace
            mask={crustMask}
            className={buildClasses("bg-surface", face)}
          />
        </div>
      </div>
    </div>
  );
}

/** A box in the level's colour, cut to its shape by a stencil of the logo. */
function LogoLevelFace(props: { mask: string; className: string }) {
  const maskImage = `url(${props.mask})`;
  return (
    <div
      className={buildClasses(
        "absolute",
        "inset-0",
        "[mask-size:contain]",
        "[mask-repeat:no-repeat]",
        "[mask-position:center]",
        props.className,
      )}
      style={{ maskImage, WebkitMaskImage: maskImage }}
    />
  );
}
